import {createClientEventId} from '../../../global-utils/helper-implementations/createClientEventId';
import {TrackingClientStateHolder} from './clientState';
import {doAsync} from './doAsync';
import {debugLog, debugWarn} from './log';

export interface DataLayerEvent {
  event: string;
  action?: string | number | null;
  label?: string | number | null;
  value?: string | number | null;
  incomplete?: true | never;
  sourceUrl?: string;
  ceid?: string;
}
export interface DataLayerEventMigration {
  GA3?: DataLayerEvent;
  GA4: DataLayerEvent;
}

export type ShouldPushToDataLayerObject =
  | DataLayerEvent
  | DataLayerEventMigration
  | 'no event data for trackProductInteraction'
  | 'expected no event data for product impressions'
  | 'no event data trackOriginClick'
  | undefined;

let asyncHandle: RequestIdleCallbackHandle | undefined;
const buffer: DataLayerEvent[] = [];

const pushEventToDataLayer = (event: DataLayerEvent) => {
  clearPreviousEcommerceObject(event.event);
  window.dataLayer.push(event);
};

const digestBuffer = () => {
  asyncHandle = undefined;

  const event = buffer.shift();
  if (event) {
    pushEventToDataLayer(event);
  }
  if (buffer.length > 0) {
    asyncHandle = doAsync(digestBuffer);
  }
};

const flushBuffer = () => {
  for (let event = buffer.shift(); event; event = buffer.shift()) {
    pushEventToDataLayer(event);
  }
};

if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
  window.addEventListener('pagehide', flushBuffer);
}

export const shouldPushToDataLayerObject = (
  event: ShouldPushToDataLayerObject,
  clientState: TrackingClientStateHolder
) => {
  const {featureFlags} = clientState.getCurrent();
  const isGA4 = featureFlags.enableGAVersion.GA4Version;

  // move all the validations into a separate function
  if (!isValidEvent(event) || event === undefined) {
    return;
  }

  const keys = Object.keys(event);
  const legacyEvent = keys.includes('event');

  if (isGA4 && !legacyEvent) {
    const {GA4} = event as DataLayerEventMigration;

    pushToDataLayer(GA4);
  }

  if (legacyEvent) {
    pushToDataLayer(event as DataLayerEvent);
  }
};

const isValidEvent = (event: ShouldPushToDataLayerObject) => {
  if (event === 'no event data for trackProductInteraction') {
    debugWarn(`unexpected ${event} event`);
    return false;
  } else if (
    event === 'no event data trackOriginClick' ||
    event === 'expected no event data for product impressions'
  ) {
    // expected case
    return false;
  }

  return true;
};

const pushToDataLayer = (event: DataLayerEvent) => {
  removeNulls(event);
  const blacklistedParams = ['code', 'user', 'id_token'];
  let modifiedSourceUrl = window.location.href;

  for (const blackListedParam of blacklistedParams) {
    if (modifiedSourceUrl.includes(blackListedParam)) {
      const urlObject = new URL(modifiedSourceUrl);
      const isParam = urlObject.searchParams.get(blackListedParam);
      if (isParam) {
        urlObject.searchParams.set(
          blackListedParam,
          `reducted_${blackListedParam}`
        );
      }
      modifiedSourceUrl = urlObject.toString();
    }
  }

  const eventToPush = Object.assign(event, {
    sourceUrl: modifiedSourceUrl,
    device: process.env.STACK,
    ceid: event.ceid ?? createClientEventId(),
  });

  buffer.push(eventToPush);

  debugLog(`${eventToPush.event}`);
  if (asyncHandle === undefined) {
    asyncHandle = doAsync(digestBuffer);
  }
};

function removeNulls<T extends {}>(data: T) {
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      delete data[key as keyof T];
    } else if (typeof value === 'object' && value != null) {
      removeNulls(value);
    }
  }
}

const eventsThatRequireEcomObjClearing = [
  'view_item_list',
  'select_item',
  'view_item',
  'add_to_cart',
  'remove_from_cart',
  'add_to_wishlist',
  'remove_from_wishlist',
  'purchase',
  'view_promotion',
  'select_promotion',
];

function clearPreviousEcommerceObject(eventName: string) {
  const shouldClearEcomObj =
    eventsThatRequireEcomObjClearing.includes(eventName);

  if (shouldClearEcomObj) {
    window.dataLayer.push({ecommerce: null, ceid: createClientEventId()});
  }
}
